import moment from "moment";
import { PricingManagementFilterInput } from "../../stores/pricing-management-analyzer.store.ts";
import { ratingToNumber } from "./analyzer.utils.ts";
import { RatingAssumption } from "../../deal-builder/services/utils/primary-constants.ts";
import { DealBuilderSavedFormat } from "../hooks/useDatabaseAPI.ts";
import { DealStatus } from "../../deal-builder/services/deal-builder.ts";

export interface DealWithStats {
  deal: DealBuilderSavedFormat;
  aaaRate: number | undefined;
  ustRate: number | undefined;
  ticSpread: number | undefined;
  aaaSpread: number | undefined;
  ustSpread: number | undefined;
  coverBid: number | undefined;
  coverBidSpread: number | undefined;
}

export async function addStatsToSavedDeals(
  deals: DealBuilderSavedFormat[],
  getAAARateForSavedDeal: (bidDate: moment.Moment, yearsToMaturity: number) => Promise<number | undefined>,
  getUSTRateForSavedDeal: (bidDate: moment.Moment, yearsToMaturity: number) => Promise<number | undefined>,
): Promise<DealWithStats[]> {
  return await Promise.all(deals.map(async deal => {
    return await calculateSavedDealStats(
      deal,
      getAAARateForSavedDeal,
      getUSTRateForSavedDeal,
    )
  }));
}

export function filterDeals(
  dealsWithStats: DealWithStats[],
  filters: PricingManagementFilterInput,
): DealWithStats[] {

  return dealsWithStats.filter(dealAndStats => {
    const deal = dealAndStats.deal;

    return (
      [DealStatus.WON, DealStatus.LOST, DealStatus.CLOSED].includes(deal.deal_status) &&
      moment(deal.bid_date).isSameOrAfter(filters.minDate, "day") &&
      moment(deal.bid_date).isSameOrBefore(filters.maxDate, "day") &&
      ratingToNumber(deal.rating_assumption as RatingAssumption) >= ratingToNumber(filters.minRating) &&
      ratingToNumber(deal.rating_assumption as RatingAssumption) <= ratingToNumber(filters.maxRating) &&
      dealAndStats.deal.average_life >= filters.minMaturity &&
      dealAndStats.deal.average_life <= filters.maxMaturity &&
      (dealAndStats.coverBid === undefined || dealAndStats.coverBid <= filters.coverBidLimit)
    )
  })

}

export async function calculateSavedDealStats(
  savedDeal: DealBuilderSavedFormat,
  getAAARateForSavedDeal: (bidDate: moment.Moment, yearsToMaturity: number) => Promise<number | undefined>,
  getUSTRateForSavedDeal: (bidDate: moment.Moment, yearsToMaturity: number) => Promise<number | undefined>,
): Promise<DealWithStats> {
  const aaaRate = await getAAARateForSavedDeal(moment(savedDeal.bid_date), Math.round(savedDeal.average_life));
  const ustRate = await getUSTRateForSavedDeal(moment(savedDeal.bid_date), Math.round(savedDeal.average_life));
  const aaaSpread = aaaRate ? savedDeal.bid_rate - aaaRate : undefined;
  const ustSpread = ustRate ? savedDeal.bid_rate - ustRate : undefined;
  const ticSpread = savedDeal.bid_rate - (savedDeal.tic || 0);
  const coverBid = savedDeal.other_bids.length === 0 ? undefined : Math.min(...savedDeal.other_bids.map(x => x[1]));
  const coverBidSpread = coverBid ? coverBid - savedDeal.bid_rate : undefined;
  return { deal: savedDeal, aaaRate, ustRate, aaaSpread, ustSpread, ticSpread, coverBid, coverBidSpread }
}

export function calculateAverages(
  deals: DealWithStats[],
  validStatuses: DealStatus[]
) {
  let totalTicSpread = 0;
  let ticSpreadEntries = 0;

  let totalAAASpread = 0;
  let aaaSpreadEntries = 0;

  let totalUSTSpread = 0;
  let ustSpreadEntries = 0;
  
  const targetDeals = deals.filter(x => validStatuses.includes(x.deal.deal_status))

  for (const deal of targetDeals) {
    if (deal.ticSpread) {
      totalTicSpread += deal.ticSpread;
      ticSpreadEntries += 1;
    }
    if (deal.aaaSpread) {
      totalAAASpread += deal.aaaSpread;
      aaaSpreadEntries += 1;
    }
    if (deal.ustSpread) {
      totalUSTSpread += deal.ustSpread;
      ustSpreadEntries += 1;
    }
  }

  const avgTicSpread = ticSpreadEntries === 0 ? undefined : totalTicSpread / ticSpreadEntries;
  const avgAAASpread = aaaSpreadEntries === 0 ? undefined : totalAAASpread / aaaSpreadEntries;
  const avgUSTSpread = ustSpreadEntries === 0 ? undefined : totalUSTSpread / ustSpreadEntries;
  
  return { avgTicSpread, avgAAASpread, avgUSTSpread };
}
